import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { MapPin, Users } from "lucide-react";
import WaitlistForm from "./WaitlistForm";
import { EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1470229538611-16ba8c7ffbd7?q=80&w=2400&auto=format&fit=crop";
const PHONE_IMG =
  "https://images.unsplash.com/photo-1506157786151-b8491531f063?q=80&w=900&auto=format&fit=crop";

const LINES = [
  { text: "What to do.", cls: "text-brand-paper" },
  { text: "Who to go with.", cls: "text-gradient" },
  { text: "How to get in.", cls: "text-brand-paper" },
];

function PhoneMockup({ mx, my }) {
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 14 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 60, damping: 14 });

  return (
    <motion.div
      className="hidden lg:block"
      style={{ perspective: 1200 }}
      initial={{ opacity: 0, y: 60, rotate: 4 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
      data-testid="hero-phone-mockup"
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="animate-[float-slow_7s_ease-in-out_infinite]"
      >
        <div className="relative w-[290px] rounded-[2.6rem] border border-brand-purple/40 bg-gradient-to-b from-[#17092e] to-black p-3 shadow-[0_40px_120px_rgba(140,82,255,0.35)]">
          <div className="overflow-hidden rounded-[2rem] bg-[#0a0612]">
            <div className="relative h-40">
              <img src={PHONE_IMG} alt="Live concert crowd" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0612] via-transparent to-brand-purple/20" />
              <span className="absolute left-3 top-3 rounded-full bg-brand-gold/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-black">
                Tonight
              </span>
            </div>
            <div className="space-y-3 p-4 text-left">
              <p className="font-display text-lg font-bold leading-tight text-brand-paper">
                Rooftop Sessions
              </p>
              <p className="flex items-center gap-1.5 text-xs text-brand-mist/60">
                <MapPin className="h-3 w-3 text-brand-teal" /> Boston · Fri · 9 PM
              </p>
              <div className="flex items-center gap-3 rounded-2xl border border-brand-purple/25 bg-black/60 p-3">
                <div className="flex -space-x-2.5" aria-hidden="true">
                  <span className="h-9 w-9 rounded-full border-2 border-[#0a0612] bg-gradient-to-br from-brand-purple to-brand-deep" />
                  <span className="h-9 w-9 rounded-full border-2 border-[#0a0612] bg-gradient-to-br from-brand-teal to-[#0d1f16]" />
                  <span className="h-9 w-9 rounded-full border-2 border-[#0a0612] bg-gradient-to-br from-brand-gold to-[#5c4200]" />
                </div>
                <div>
                  <p className="flex items-center gap-1 text-xs font-bold text-brand-paper">
                    <Users className="h-3.5 w-3.5 text-brand-teal" /> See who&apos;s going
                  </p>
                  <p className="mt-0.5 text-[10px] text-brand-mist/50">Your crew, coordinated</p>
                </div>
              </div>
              <div className="rounded-full bg-brand-teal py-2.5 text-center text-xs font-bold text-black">
                View event
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden pt-[72px]"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className="h-full w-full object-cover opacity-50 saturate-[0.9]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#1a0640]/55 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-[#2b0086]/30" />
      </motion.div>

      <div
        className="pointer-events-none absolute -left-40 top-1/3 h-[560px] w-[560px] rounded-full bg-[#2b0086] opacity-40 blur-[160px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-32 top-10 h-[460px] w-[460px] rounded-full bg-brand-purple opacity-25 blur-[150px]"
        aria-hidden="true"
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-[1.5fr_1fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="mb-7 inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-gold"
              data-testid="hero-badge"
            >
              For stagehands, production companies &amp; crew
            </motion.p>

            <h1 className="font-display font-extrabold uppercase leading-[0.98] tracking-tight">
              {LINES.map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block text-[clamp(2.4rem,5.5vw,5.4rem)] ${line.cls}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1.05, delay: 0.18 + i * 0.13, ease: EASE }}
                    data-testid={`hero-line-${i + 1}`}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-7 max-w-xl text-base leading-relaxed text-brand-mist/75 sm:text-lg"
            >
              Lantix surfaces Boston's live events and the people who make them happen:
              stagehands, production companies and crew. Discovery, coordination and connection,
              all in one place. A behavioral layer for live experiences, built for Boston.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-9 max-w-xl"
            >
              <WaitlistForm idPrefix="hero-waitlist" source="hero" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium uppercase tracking-[0.18em] text-brand-mist/50"
            >
              <span data-testid="hero-chip-boston">Launching in Boston</span>
              <span className="h-1 w-1 rounded-full bg-brand-gold/70" />
              <span data-testid="hero-chip-stage">$4M seed raise</span>
              <span className="h-1 w-1 rounded-full bg-brand-gold/70" />
              <span data-testid="hero-chip-mvp">MVP in development</span>
            </motion.div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <PhoneMockup mx={mx} my={my} />
          </div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        aria-hidden="true"
      >
        <div className="h-12 w-[1px] overflow-hidden bg-white/15">
          <motion.div
            className="h-1/2 w-full bg-brand-teal"
            animate={{ y: ["-100%", "220%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
